import { memo, useCallback, useMemo } from 'react';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { getResultDecisionActId } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { IErknmResultDecisionActSelectOption } from 'KnmErknmPackage/MainModule/interfaces/IErknmResultDecisionActSelectOption';
import { generateOrganizationTemplateText } from 'KnmErknmPackage/MainModule/common/generateOrganizationTemplateText';
import { IKnmErknmFormResultDecision } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormResultDecision';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErknmForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';
import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { Hint } from 'CorePackage/MainModule/components/Hint';

import styles from './ResultDecisionsItemContentActs.module.scss';

interface IResultDecisionsItemContentActsProps {
  resultDecisionId: string;
  name: string;
  acts: IKnmErknmFormAct[] | undefined;
  watchOnly: boolean;
  resultDecisions: IKnmErknmFormResultDecision[] | undefined;
  formRef: IKnmFormRef | undefined;
}

export const ResultDecisionsItemContentActs = memo(
  ({ resultDecisionId, name, acts, watchOnly, resultDecisions, formRef }: IResultDecisionsItemContentActsProps) => {
    const actSelectOptions = useMemo(() => {
      return acts
        ?.filter(act => act.familiarizationNotifications?.length && act.violatedMandatoryRequirements)
        .map(act => {
          return {
            id: act.guid || '',
            value: act.guid,
            label: `${act.numberAct} - ${generateOrganizationTemplateText(
              act.organization?.inn || undefined,
              act.organization?.ogrn || undefined,
              act.organization?.organizationName || undefined,
              act.organization?.rafpNza || undefined,
            )}`,
            isDisabled: resultDecisions?.some(decision => decision?.act?.id === act.guid),
          } as IErknmResultDecisionActSelectOption;
        });
    }, [acts, resultDecisions]);

    const onChangeActSelect = useCallback(
      (value: IErknmResultDecisionActSelectOption) => {
        if (value?.id) {
          const countResultDecisionWithSelectedAct =
            resultDecisions?.filter(
              rd =>
                rd?.act?.id === value?.id &&
                (rd.kindDecision?.code === KindResultDecisionCodesEnum.D_1 ||
                  rd.kindDecision?.code === KindResultDecisionCodesEnum.D_7),
            ).length || 0;
          const selectedAct = acts?.find(i => i.guid === value.id);

          formRef?.current.change(
            `${name}.numberDecision` as keyof IKnmErknmForm,
            `${selectedAct?.numberAct}-${countResultDecisionWithSelectedAct + 1}`,
          );
          formRef?.current.change(`${name}.organization` as keyof IKnmErknmForm, selectedAct?.organization);
          formRef?.current.change(
            `${name}.organizationTemplate` as keyof IKnmErknmForm,
            selectedAct?.organizationTemplate,
          );
          formRef?.current.change(`${name}.violatedMandatoryRequirements` as keyof IKnmErknmForm);
        }
      },
      [formRef, resultDecisions, name, acts],
    );

    const isROKindKnm = formRef?.current.getFieldState('kindKnm')?.value?.code === KnmKindCodesEnum.RO;
    const isShowHintByFamiliarizationNotifications = useMemo(() => {
      return !acts?.every(item => item?.familiarizationNotifications && item?.familiarizationNotifications?.length > 0);
    }, [acts]);

    return (
      <FieldBlock label={'Акт'} className={styles.GroupMarginBottom}>
        <FieldBlock id={getResultDecisionActId(resultDecisionId)}>
          <SelectInputField
            name={`${name}.act`}
            placeholder="Выберите акт"
            size="medium"
            options={actSelectOptions}
            disabled={watchOnly || acts?.length === 1}
            onChange={onChangeActSelect}
          />
        </FieldBlock>
        {isROKindKnm && isShowHintByFamiliarizationNotifications && (
          <Hint text="Выбор акта возможен только после внесения сведений об уведомлении об акте" />
        )}
      </FieldBlock>
    );
  },
);
